import React from "react";
import type { HealthView } from "../lib/topologyAdapter";

interface HealthPillConfig {
  glyph: string;
  label: string;
  background: string;
  color: string;
}

// Color + glyph + label together — health is never conveyed by color alone
// (a11y). 'unknown' gets its own distinct gray tokens so a stale reading is
// never confusable with a healthy one.
export const HEALTH_PILL_CONFIG: Record<HealthView, HealthPillConfig> = {
  good: {
    glyph: "●", // ●
    label: "HEALTHY",
    background: "var(--color-status-available-bg)",
    color: "var(--color-status-available-text)",
  },
  warning: {
    glyph: "▲", // ▲
    label: "WARNING",
    background: "var(--color-status-busy-bg)",
    color: "var(--color-status-busy-text)",
  },
  critical: {
    glyph: "✕", // ✕
    label: "CRITICAL",
    background: "var(--color-status-offline-bg)",
    color: "var(--color-status-offline-text)",
  },
  unknown: {
    glyph: "◌", // ◌
    label: "STALE",
    background: "var(--color-foreground)",
    color: "var(--color-neutral)",
  },
};

export interface HealthPillProps {
  status: HealthView;
}

export default function HealthPill({ status }: HealthPillProps) {
  const cfg = HEALTH_PILL_CONFIG[status] ?? HEALTH_PILL_CONFIG.unknown;
  return (
    <span
      role="status"
      aria-label={cfg.label}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: "var(--font-size-xs)",
        fontWeight: 700,
        padding: "2px var(--space-sm)",
        borderRadius: "var(--radius-pill)",
        textTransform: "uppercase",
        letterSpacing: "0.3px",
        whiteSpace: "nowrap",
        background: cfg.background,
        color: cfg.color,
      }}
    >
      <span aria-hidden="true">{cfg.glyph}</span>
      {cfg.label}
    </span>
  );
}
